// Value at Risk (VAR) for loss adjusters: the rebuild or replacement value of what is insured,
// compared with the sum insured to show whether the policy is adequate. Crawford's worked method
// (Shep, 2026-09-04; Stuart Cannon on the 11 Sep call): each building part is
// length × width × storeys × rate per m², plus rounded figures for boundary walls, drives and
// other costs; contents-like sections are a list of amounts. Adequacy = sum insured ÷ total × 100.
//
// Money is exact: inputs are decimal strings scaled to integers, never floats, because these
// figures go into a report an insurer acts on.

export const VALUE_AT_RISK_TABLE_KIND = 'value-at-risk';

export type ValueAtRiskSectionKind = 'Buildings' | 'Contents' | 'Stock' | 'MachineryAndPlant' | 'Other';

export const VALUE_AT_RISK_SECTION_LABELS: Record<ValueAtRiskSectionKind, string> = {
  Buildings: 'Buildings',
  Contents: 'Contents',
  Stock: 'Stock',
  MachineryAndPlant: 'Machinery & Plant',
  Other: 'Other',
};

export const DEFAULT_VALUE_AT_RISK_THRESHOLD = '85';

type Decimal = string | null | undefined;

export interface ValueAtRiskBuildingRowInput {
  description: string;
  length?: Decimal;
  width?: Decimal;
  storeys?: Decimal;
  rate?: Decimal;
}

export interface ValueAtRiskAmountRowInput {
  description: string;
  amount?: Decimal;
}

export interface ValueAtRiskSectionInput {
  kind: ValueAtRiskSectionKind;
  /** Shown instead of the kind's label; required for Other. */
  name?: string | null;
  sumInsured?: Decimal;
  sumInsuredUnlimited?: boolean | null;
  /** Buildings sections read length/width/storeys/rate; every other kind reads amount. */
  rows: Array<ValueAtRiskBuildingRowInput & ValueAtRiskAmountRowInput>;
  boundaryWalls?: Decimal;
  drives?: Decimal;
  otherCosts?: Decimal;
}

export interface ValueAtRiskInput {
  /** Percent at or above which the sum insured counts as adequate. */
  thresholdPercent?: Decimal;
  sections: ValueAtRiskSectionInput[];
}

export type ValueAtRiskVerdict = 'Adequate' | 'Inadequate';

export interface ValueAtRiskSectionResult {
  label: string;
  /** Per row, in pounds with 2 decimals; null when the row is missing a value. */
  rowTotals: Array<string | null>;
  /** Sum of the complete rows plus the extras. */
  total: string;
  /** True when any row is incomplete, so total is a floor, not the value at risk. */
  partial: boolean;
  /** Percent with 2 decimals; null when it can't be stated. */
  adequacyPercent: string | null;
  verdict: ValueAtRiskVerdict | null;
}

export interface ValueAtRiskResult {
  thresholdPercent: string;
  sections: ValueAtRiskSectionResult[];
}

export class ValueAtRiskInputError extends Error {}

// Inputs keep up to 4 decimal places; money is reported in pence.
const INPUT_SCALE = 10_000n;
const PENCE_PER_POUND = 100n;

function isBlank(value: Decimal): boolean {
  return value === null || value === undefined || value.trim() === '';
}

/** Parses "1,072,122.00", "£2,500" or "8" into an integer scaled by INPUT_SCALE. */
function parseDecimal(value: Decimal, field: string): bigint | null {
  if (isBlank(value)) return null;
  const cleaned = value!.replace(/[£,\s]/g, '');
  const match = /^(\d+)(?:\.(\d{1,4}))?$/.exec(cleaned);
  if (!match) {
    throw new ValueAtRiskInputError(`${field} must be a number of 0 or more with up to 4 decimals, not "${value}".`);
  }
  return BigInt(match[1]) * INPUT_SCALE + BigInt((match[2] ?? '').padEnd(4, '0'));
}

/** Integer division rounding half up; both operands are non-negative. */
function divideRounded(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

function formatHundredths(value: bigint): string {
  return `${value / 100n}.${(value % 100n).toString().padStart(2, '0')}`;
}

function toPence(scaled: bigint): bigint {
  return divideRounded(scaled * PENCE_PER_POUND, INPUT_SCALE);
}

function sectionLabel(section: ValueAtRiskSectionInput): string {
  const name = section.name?.trim();
  if (section.kind === 'Other' && !name) throw new ValueAtRiskInputError('An Other section needs a name.');
  return name || VALUE_AT_RISK_SECTION_LABELS[section.kind];
}

type ValueAtRiskRowInput = ValueAtRiskSectionInput['rows'][number];

function rowTotalPence(section: ValueAtRiskSectionInput, row: ValueAtRiskRowInput, index: number): bigint | null {
  const at = `${sectionLabel(section)} row ${index + 1}`;
  if (section.kind !== 'Buildings') {
    const amount = parseDecimal(row.amount, `${at} amount`);
    return amount === null ? null : toPence(amount);
  }
  const factors = [
    parseDecimal(row.length, `${at} length`),
    parseDecimal(row.width, `${at} width`),
    parseDecimal(row.storeys, `${at} storeys`),
    parseDecimal(row.rate, `${at} rate`),
  ];
  if (factors.some(factor => factor === null)) return null;
  const product = factors.reduce((acc, factor) => acc! * factor!, 1n)!;
  return divideRounded(product * PENCE_PER_POUND, INPUT_SCALE ** 4n);
}

const ROW_FIGURES = ['length', 'width', 'storeys', 'rate', 'amount'] as const;

function hasFigures(section: ValueAtRiskSectionInput): boolean {
  return section.rows.some(row => ROW_FIGURES.some(field => !isBlank(row[field])))
    || [section.boundaryWalls, section.drives, section.otherCosts].some(extra => !isBlank(extra));
}

/**
 * Why this input can't be saved yet, or null. A section with no figures would reach the report as
 * an empty table; a partly filled one still saves and reads as incomplete. Kept apart from
 * computeValueAtRisk so the form can preview from its first keystroke.
 */
export function valueAtRiskMissingFigures(input: ValueAtRiskInput): string | null {
  const empty = input.sections.find(section => !hasFigures(section));
  return empty ? `Add a figure to ${sectionLabel(empty)}, or remove the section.` : null;
}

export function computeValueAtRisk(input: ValueAtRiskInput): ValueAtRiskResult {
  const thresholdScaled = parseDecimal(input.thresholdPercent, 'Threshold')
    ?? parseDecimal(DEFAULT_VALUE_AT_RISK_THRESHOLD, 'Threshold')!;

  const sections = input.sections.map((section): ValueAtRiskSectionResult => {
    const label = sectionLabel(section);
    const rows = section.rows.map((row, index) => rowTotalPence(section, row, index));
    const extras = section.kind === 'Buildings'
      ? [section.boundaryWalls, section.drives, section.otherCosts]
        .map((extra, index) => parseDecimal(extra, `${label} ${['boundary walls', 'drives', 'other costs'][index]}`))
        .map(extra => (extra === null ? 0n : toPence(extra)))
      : [];
    const totalPence = [...rows, ...extras].reduce<bigint>((acc, pence) => acc + (pence ?? 0n), 0n);
    const partial = rows.some(pence => pence === null);
    const sumInsured = parseDecimal(section.sumInsured, `${label} sum insured`);

    let adequacyPercent: string | null = null;
    let verdict: ValueAtRiskVerdict | null = null;
    if (section.sumInsuredUnlimited) {
      verdict = 'Adequate';
    } else if (!partial && sumInsured !== null && totalPence > 0n) {
      // Hundredths of a percent: SI pence × 10,000 ÷ total pence.
      const hundredths = divideRounded(toPence(sumInsured) * 10_000n, totalPence);
      adequacyPercent = formatHundredths(hundredths);
      verdict = hundredths * INPUT_SCALE >= thresholdScaled * 100n ? 'Adequate' : 'Inadequate';
    }

    return {
      label,
      rowTotals: rows.map(pence => (pence === null ? null : formatHundredths(pence))),
      total: formatHundredths(totalPence),
      partial,
      adequacyPercent,
      verdict,
    };
  });

  return { thresholdPercent: formatHundredths(divideRounded(thresholdScaled * 100n, INPUT_SCALE)), sections };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** "719780.00" → "£719,780.00". */
export function formatPounds(value: string): string {
  const [whole, pence] = value.split('.');
  return `£${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${pence}`;
}

function cell(value: string, header = false, colspan = 1): string {
  const tag = header ? 'th' : 'td';
  const span = colspan > 1 ? ` colspan="${colspan}"` : '';
  return `<${tag}${span}>${escapeHtml(value)}</${tag}>`;
}

function row(cells: string[], header = false): string {
  return `<tr>${cells.map(value => cell(value, header)).join('')}</tr>`;
}

/**
 * A label across every column but the last, and its value in the last so totals line up under
 * "Total". Spanning keeps "Total value at risk" from wrapping in the narrow description column.
 */
function labelledRow(columns: number, label: string, value: string): string {
  return `<tr>${cell(label, false, columns - 1)}${cell(value)}</tr>`;
}

function adequacyText(section: ValueAtRiskSectionInput, result: ValueAtRiskSectionResult, threshold: string): string {
  if (section.sumInsuredUnlimited) return 'Unlimited sum insured: Adequate';
  if (result.partial) return 'Not calculated: a row is missing a value';
  if (result.adequacyPercent === null) return 'Not calculated: sum insured not given';
  return `${result.adequacyPercent}% (threshold ${threshold}%): ${result.verdict}`;
}

/**
 * One table per section, each marked with data-table-kind so the report can be found and
 * replaced as a block. The marker sits on every table rather than on a wrapper element because
 * the report editor drops elements it doesn't know.
 */
export function renderValueAtRiskHtml(input: ValueAtRiskInput, result: ValueAtRiskResult): string {
  return input.sections.map((section, index) => {
    const sectionResult = result.sections[index];
    const isBuildings = section.kind === 'Buildings';
    const columns = isBuildings ? 6 : 2;
    const extras = isBuildings
      ? ([
        ['Boundary walls', section.boundaryWalls],
        ['Drives, paths and externals', section.drives],
        ['Other (drainage, fees, etc.)', section.otherCosts],
      ] as const)
        .filter(([, value]) => !isBlank(value))
        .map(([label, value]) => labelledRow(columns, label, formatPounds(poundsOf(value))))
      : [];
    const sumInsured = section.sumInsuredUnlimited ? 'Unlimited'
      : isBlank(section.sumInsured) ? 'Not given' : formatPounds(poundsOf(section.sumInsured));
    const lines = [
      `<tr>${cell(`Value at Risk: ${sectionResult.label}`, true, columns)}</tr>`,
      row(isBuildings ? ['Description', 'Length (m)', 'Width (m)', 'Storeys', 'Rate (£/m²)', 'Total'] : ['Description', 'Amount'], true),
      ...section.rows.map((rowInput, rowIndex) => {
        const total = sectionResult.rowTotals[rowIndex];
        const totalText = total === null ? 'Missing value' : formatPounds(total);
        return row(isBuildings
          ? [rowInput.description, rowInput.length ?? '', rowInput.width ?? '', rowInput.storeys ?? '', rowInput.rate ?? '', totalText]
          : [rowInput.description, totalText]);
      }),
      ...extras,
      labelledRow(columns, 'Total value at risk', `${formatPounds(sectionResult.total)}${sectionResult.partial ? ' (incomplete)' : ''}`),
      labelledRow(columns, 'Sum insured', sumInsured),
      labelledRow(columns, 'Adequacy of sum insured', adequacyText(section, sectionResult, result.thresholdPercent)),
    ];
    return `<table data-table-kind="${VALUE_AT_RISK_TABLE_KIND}"><tbody>${lines.join('')}</tbody></table>`;
  }).join('');
}

function poundsOf(value: Decimal): string {
  return formatHundredths(toPence(parseDecimal(value, 'Amount')!));
}

const VALUE_AT_RISK_TABLE_PATTERN = new RegExp(
  `<table\\b[^>]*\\bdata-table-kind=(["'])${VALUE_AT_RISK_TABLE_KIND}\\1[^>]*>[\\s\\S]*?</table>`,
  'gi',
);

/**
 * Puts the Value at Risk block into a report, replacing any earlier one so a report never carries
 * two. The new block goes where the first old table was, so an adjuster who moved it keeps their
 * placement; a report without one gets it at the end.
 */
export function spliceValueAtRiskHtml(reportHtml: string, blockHtml: string): string {
  let inserted = false;
  const replaced = reportHtml.replace(VALUE_AT_RISK_TABLE_PATTERN, () => {
    if (inserted) return '';
    inserted = true;
    return blockHtml;
  });
  return inserted ? replaced : `${reportHtml}${blockHtml}`;
}
